import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ETIQUETAS_CONDICION_PAGO,
  formatearCLP,
  formatearPorcentajeSimple,
  type CondicionPago,
} from '@lexicore/shared';
import {
  IconoBuscar,
  IconoCerrar,
  IconoDerecha,
  IconoDescargar,
  IconoWhatsapp,
  IconoCheck,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { descargar } from '../lib/api';
import { useCartera, useFacturas, type Factura } from '../lib/consultas';
import { ModalPagarFacturaRapido } from './cobranza/ModalPagarFacturaRapido';
import { ModalGestionCobro } from './cobranza/ModalGestionCobro';
import { useAvisos } from '../contextos/AvisosContext';

type FiltroBatalla = 'todas' | 'vencidas' | 'porCobrar';

const FILTROS: { clave: FiltroBatalla; etiqueta: string; params: Record<string, boolean> }[] = [
  { clave: 'todas', etiqueta: 'Todas las facturas', params: {} },
  { clave: 'vencidas', etiqueta: '🔴 Solo Vencidas (Cobranza Inmediata)', params: { soloVencidas: true } },
  { clave: 'porCobrar', etiqueta: '⏳ Solo Pendientes de Cobro', params: { soloPorCobrar: true } },
];

export function Cobranza() {
  const avisos = useAvisos();
  const navegar = useNavigate();

  // Estados de filtrado y búsqueda
  const [filtroRapido, setFiltroRapido] = useState<FiltroBatalla>('todas');
  const [busqueda, setBusqueda] = useState('');
  const [organismoFiltroId, setOrganismoFiltroId] = useState<string | null>(null);
  const [organismoFiltroNombre, setOrganismoFiltroNombre] = useState<string>('');

  // Modales de acción 1-clic
  const [facturaParaPagar, setFacturaParaPagar] = useState<Factura | null>(null);
  const [facturaParaCobrar, setFacturaParaCobrar] = useState<Factura | null>(null);

  // Consultas
  const cartera = useCartera();
  const facturas = useFacturas({
    ...FILTROS.find((f) => f.clave === filtroRapido)!.params,
    organismoId: organismoFiltroId || undefined,
    busqueda: busqueda.trim() || undefined,
    porPagina: 100,
  });

  const handleDescargarCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroRapido === 'vencidas') params.set('soloVencidas', 'true');
      if (filtroRapido === 'porCobrar') params.set('soloPorCobrar', 'true');
      if (organismoFiltroId) params.set('organismoId', organismoFiltroId);

      const qs = params.toString() ? `?${params.toString()}` : '';
      const nombreArchivo =
        filtroRapido === 'vencidas'
          ? 'cartera_vencida_lexicore.csv'
          : 'cartera_cobranza_lexicore.csv';

      await descargar(`/facturas/exportar${qs}`, nombreArchivo);
      avisos.exito('Cartera exportada a CSV exitosamente.');
    } catch {
      avisos.error('No se pudo descargar el archivo CSV.');
    }
  };

  if (cartera.isLoading) return <Cargando texto="Calculando cartera y vencimientos…" />;
  if (cartera.isError) return <ErrorCarga error={cartera.error} reintentar={() => void cartera.refetch()} />;
  if (!cartera.data) return null;

  // Extracción tolerante a versiones antiguas y nuevas del backend
  const data = cartera.data;
  const totalFacturado = data.kpis?.totalFacturado ?? data.totalFacturado ?? 0;
  const totalCobrado = data.kpis?.totalCobrado ?? data.totalCobrado ?? 0;
  const totalPorCobrar = data.kpis?.porCobrar ?? data.antiguedad?.totalPorCobrar ?? 0;
  const montoVencido = data.kpis?.montoVencido ?? data.antiguedad?.totalVencido ?? 0;
  const facturasVencidasCount = data.kpis?.facturasVencidasCount ?? 0;
  const dso = data.kpis?.diasPromedioCobro ?? data.diasPromedioCobro ?? null;

  const tramosAging = data.agingCuatroTramos ?? data.antiguedad?.tramos ?? [];
  const topDeudores = data.topDeudores ?? [];

  const sinDatos = totalFacturado === 0;

  return (
    <>
      {/* Cabecera del Módulo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
            Facturación & Cobranza a Organismos Públicos
          </h1>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Consola ejecutiva de cartera estatal, aging en 4 tramos, DSO real ponderado y cobranza protocolar en 1-clic.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDescargarCsv}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconoDescargar size={14} />
            <span>Exportar CSV (Excel)</span>
          </button>
        </div>
      </div>

      {/* Barra de 4 KPIs Ejecutivos + DSO */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Total Facturado"
          valor={formatearCLP(totalFacturado)}
          acento="var(--accent-blue)"
          nota={`${data.kpis?.facturasTotalesCount ?? facturas.data?.total ?? 0} documentos emitidos`}
        />
        <TarjetaKpi
          etiqueta="Total Cobrado"
          valor={formatearCLP(totalCobrado)}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota={
            totalFacturado > 0
              ? `${formatearPorcentajeSimple((totalCobrado / totalFacturado) * 100, 1)} de lo facturado`
              : 'Sin facturación'
          }
        />
        <TarjetaKpi
          etiqueta="Por Cobrar (Cartera Viva)"
          valor={formatearCLP(totalPorCobrar)}
          acento="var(--accent-yellow)"
          colorValor="var(--accent-yellow)"
          insignia={
            montoVencido > 0
              ? {
                  texto: `${formatearCLP(montoVencido)} vencido (${facturasVencidasCount} doc)`,
                  color: 'var(--accent-red)',
                  fondo: 'var(--accent-red-soft)',
                }
              : undefined
          }
        />
        <TarjetaKpi
          etiqueta="DSO (Días Promedio Cobro)"
          valor={dso === null ? '—' : `${Math.round(dso)} días`}
          acento={dso !== null && dso > 45 ? 'var(--accent-red)' : 'var(--accent-gray)'}
          colorValor={dso !== null && dso > 45 ? 'var(--accent-red)' : undefined}
          nota={dso === null ? 'Aún sin cobros completados' : 'Ponderado por volumen financiero'}
        />
      </div>

      {sinDatos ? (
        <Panel>
          <Vacio
            titulo="Todavía no hay facturas emitidas"
            detalle="Cada proyecto adjudicado genera su cronograma de facturación. En la ficha de cada proyecto puedes emitir facturas y administrar sus pagos."
          />
        </Panel>
      ) : (
        <>
          {/* Matriz de Antigüedad (Aging 4 Tramos) y Concentración Pareto de Deudores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Panel Aging 4 Tramos */}
            <Panel
              titulo="Matriz de Antigüedad de Deuda (Aging)"
              subtitulo="Distribución de la cartera viva según estado de vencimiento"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {tramosAging.map((tramo: any) => {
                  const maximo = Math.max(...tramosAging.map((t: any) => Math.abs(t.monto)), 1);
                  const esCritico = tramo.clave === 'CRITICAS_60_MAS' || tramo.clave === 'D_90_MAS' || tramo.clave === 'D_61_90';
                  const esNaranja = tramo.clave === 'VENCIDAS_31_60' || tramo.clave === 'D_31_60';
                  const esAmarillo = tramo.clave === 'VENCIDAS_1_30' || tramo.clave === 'D_1_30';
                  const esPorVencer = tramo.clave === 'POR_VENCER';

                  const colorTexto = esCritico
                    ? 'var(--accent-red)'
                    : esNaranja
                    ? '#ea580c'
                    : esAmarillo
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-green)';

                  const gradiente = esCritico
                    ? 'linear-gradient(90deg, var(--accent-red), #991b1b)'
                    : esNaranja
                    ? 'linear-gradient(90deg, #f97316, var(--accent-red))'
                    : esAmarillo
                    ? 'linear-gradient(90deg, var(--accent-yellow), #f59e0b)'
                    : 'linear-gradient(90deg, var(--accent-green), var(--accent-blue))';

                  return (
                    <div key={tramo.clave} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 600 }}>
                            {tramo.etiqueta}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 6,
                              background: esPorVencer ? 'var(--accent-green-soft)' : 'var(--bg-elevated)',
                              color: colorTexto,
                            }}
                          >
                            {tramo.cantidad} {tramo.cantidad === 1 ? 'factura' : 'facturas'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                          {tramo.porcentaje !== undefined && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {tramo.porcentaje}%
                            </span>
                          )}
                          <span className="td-mono" style={{ fontSize: 13.5, fontWeight: 700, color: colorTexto }}>
                            {formatearCLP(tramo.monto)}
                          </span>
                        </div>
                      </div>
                      <div className="budget-track" style={{ height: 6 }}>
                        <div
                          className="budget-fill"
                          style={{
                            width: `${Math.min(100, (Math.abs(tramo.monto) / maximo) * 100)}%`,
                            background: gradiente,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>

            {/* Panel Top Deudores (Pareto 80/20) */}
            <Panel
              titulo="Top Deudores de Cartera (Pareto 80/20)"
              subtitulo="Instituciones con mayor saldo retenido y acceso rápido a contacto"
            >
              {topDeudores.length === 0 ? (
                <Vacio titulo="Sin deuda pendiente acumulada" detalle="Todas las facturas emitidas han sido pagadas." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 290, overflowY: 'auto' }}>
                  {topDeudores.slice(0, 5).map((deudor: any) => {
                    const saldoDeuda = deudor.montoDeuda ?? deudor.saldo ?? 0;
                    const facturasCount = deudor.facturasPendientesCount ?? deudor.facturas ?? 1;
                    const estaFiltrado = organismoFiltroId === deudor.organismoId;

                    return (
                      <div
                        key={deudor.organismoId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          padding: '10px 12px',
                          background: estaFiltrado ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                          border: `1px solid ${estaFiltrado ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                          borderRadius: 'var(--radius-sm)',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={deudor.organismoNombre}
                          >
                            {deudor.organismoNombre}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {facturasCount} doc(s) · {deudor.porcentajeDeuda ?? '—'}% de la deuda
                            {deudor.contactoPrincipal?.nombre && (
                              <span> · Contacto: {deudor.contactoPrincipal.nombre}</span>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="td-mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-yellow)' }}>
                            {formatearCLP(saldoDeuda)}
                          </span>

                          {/* Botón WhatsApp directo si existe enlace */}
                          {deudor.contactoPrincipal?.enlaceWhatsapp && (
                            <a
                              href={deudor.contactoPrincipal.enlaceWhatsapp}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm"
                              style={{
                                padding: '4px 8px',
                                background: '#25D366',
                                color: '#fff',
                                borderColor: '#25D366',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 11,
                              }}
                              title="Abrir chat en WhatsApp"
                            >
                              <IconoWhatsapp size={12} />
                            </a>
                          )}

                          {/* Botón para filtrar en la tabla */}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '4px 8px', fontSize: 11 }}
                            onClick={() => {
                              if (estaFiltrado) {
                                setOrganismoFiltroId(null);
                                setOrganismoFiltroNombre('');
                              } else {
                                setOrganismoFiltroId(deudor.organismoId);
                                setOrganismoFiltroNombre(deudor.organismoNombre);
                              }
                            }}
                          >
                            {estaFiltrado ? 'Quitar filtro' : 'Filtrar'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>

          {/* Barra de Filtros de Batalla y Búsqueda Instantánea */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginBottom: 16,
              alignItems: 'center',
              flexWrap: 'wrap',
              background: 'var(--bg-elevated)',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            {/* Píldoras de Filtro Rápido */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {FILTROS.map((opcion) => {
                const activo = filtroRapido === opcion.clave;
                return (
                  <button
                    key={opcion.clave}
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setFiltroRapido(opcion.clave)}
                    style={{
                      background: activo ? 'var(--accent-blue-soft)' : 'transparent',
                      color: activo ? 'var(--accent-blue)' : 'var(--text-secondary)',
                      borderColor: activo ? 'var(--accent-blue)' : 'var(--border-subtle)',
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: activo ? 700 : 500,
                    }}
                  >
                    {opcion.etiqueta}
                  </button>
                );
              })}
            </div>

            {/* Búsqueda Textual */}
            <div style={{ position: 'relative', minWidth: 260, flexGrow: 1 }}>
              <span
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              >
                <IconoBuscar size={14} />
              </span>
              <input
                type="text"
                placeholder="Buscar por folio, orden de compra o proyecto…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{
                  paddingLeft: 32,
                  width: '100%',
                  fontSize: 12,
                  height: 34,
                }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <IconoCerrar size={13} />
                </button>
              )}
            </div>

            {/* Filtro Activo de Organismo */}
            {organismoFiltroId && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'var(--accent-blue-soft)',
                  color: 'var(--accent-blue)',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                <span>Organismo: {organismoFiltroNombre}</span>
                <button
                  type="button"
                  onClick={() => {
                    setOrganismoFiltroId(null);
                    setOrganismoFiltroNombre('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                >
                  <IconoCerrar size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Tabla de Facturas */}
          <div className="tabla-wrap">
            <div className="tabla-scroll">
              <table style={{ minWidth: 1180 }}>
                <thead>
                  <tr>
                    <th>Folio & OC</th>
                    <th>Institución Compradora & Proyecto</th>
                    <th>Emisión & Vencimiento</th>
                    <th style={{ textAlign: 'right' }}>Neto</th>
                    <th style={{ textAlign: 'right' }}>Total Facturado</th>
                    <th style={{ textAlign: 'right' }}>Cobrado</th>
                    <th style={{ textAlign: 'right' }}>Saldo Pendiente</th>
                    <th>Situación / Mora</th>
                    <th style={{ textAlign: 'center' }}>Acciones 1-Clic</th>
                  </tr>
                </thead>
                <tbody>
                  {facturas.isLoading && (
                    <tr>
                      <td colSpan={9}>
                        <Cargando texto="Cargando facturas..." />
                      </td>
                    </tr>
                  )}

                  {!facturas.isLoading && facturas.data?.datos.length === 0 && (
                    <tr>
                      <td colSpan={9}>
                        <Vacio
                          titulo="Ninguna factura coincide con los filtros aplicados"
                          detalle="Intenta cambiar los filtros de búsqueda o seleccionar 'Todas las facturas'."
                        />
                      </td>
                    </tr>
                  )}

                  {facturas.data?.datos.map((factura) => {
                    const pagado = factura.montoPagado ?? factura.situacion?.montoPagado ?? 0;
                    const saldo =
                      factura.saldoPendiente ??
                      factura.situacion?.saldo ??
                      Math.max(0, factura.montoTotal - pagado);
                    const diasMora = factura.diasMora ?? factura.situacion?.diasAtraso ?? 0;
                    const estaVencida = factura.estaVencida ?? factura.situacion?.estaVencida ?? (diasMora > 0 && saldo > 0);
                    const pctPagado = factura.montoTotal > 0 ? (pagado / factura.montoTotal) * 100 : 0;
                    const estadoCobro = factura.situacionCobro ?? factura.situacion?.estado ?? factura.estado;

                    return (
                      <tr key={factura.id}>
                        {/* Folio & OC */}
                        <td>
                          <div
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: 'var(--text-primary)',
                            }}
                          >
                            {factura.numero ? `#${factura.numero}` : 's/n'}
                          </div>
                          {factura.numeroOrdenCompra && (
                            <div style={{ fontSize: 10.5, color: 'var(--accent-blue)', marginTop: 2, fontWeight: 600 }}>
                              OC: {factura.numeroOrdenCompra}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            {factura.tipoDocumento === 'FACTURA_ELECTRONICA' ? 'Factura Electrónica' : factura.tipoDocumento}
                          </div>
                        </td>

                        {/* Institución & Proyecto */}
                        <td>
                          <div
                            style={{
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              fontSize: 12,
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setOrganismoFiltroId(factura.organismo.id);
                              setOrganismoFiltroNombre(factura.organismo.nombre);
                            }}
                            title="Haz clic para filtrar solo por este organismo"
                          >
                            {factura.organismo.nombre}
                          </div>
                          {factura.proyecto ? (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              <span style={{ color: 'var(--text-secondary)' }}>{factura.proyecto.codigo}</span> · {factura.proyecto.nombre}
                            </div>
                          ) : (
                            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>Sin proyecto asignado</div>
                          )}
                        </td>

                        {/* Fechas */}
                        <td style={{ fontSize: 11 }}>
                          <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            Emisión: {factura.fechaEmision.slice(0, 10)}
                          </div>
                          <div style={{ fontFamily: 'var(--font-mono)', color: estaVencida ? 'var(--accent-red)' : 'var(--text-muted)', marginTop: 2 }}>
                            Vence: {factura.fechaVencimiento.slice(0, 10)}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {ETIQUETAS_CONDICION_PAGO[factura.condicionPago as CondicionPago] ?? factura.condicionPago}
                          </div>
                        </td>

                        {/* Montos */}
                        <td className="td-mono" style={{ textAlign: 'right', fontSize: 12 }}>
                          {formatearCLP(factura.montoNeto)}
                        </td>

                        <td className="td-mono" style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {formatearCLP(factura.montoTotal)}
                        </td>

                        {/* Cobrado */}
                        <td className="td-mono" style={{ textAlign: 'right', color: 'var(--accent-green)', fontSize: 12 }}>
                          <div>{formatearCLP(pagado)}</div>
                          <div className="progress-track" style={{ height: 4, marginTop: 4 }}>
                            <div
                              className="progress-fill"
                              style={{ width: `${pctPagado}%`, background: 'var(--accent-green)' }}
                            />
                          </div>
                        </td>

                        {/* Saldo */}
                        <td
                          className="td-mono"
                          style={{
                            textAlign: 'right',
                            fontSize: 13,
                            fontWeight: 700,
                            color: saldo > 0 ? 'var(--accent-yellow)' : 'var(--text-muted)',
                          }}
                        >
                          {formatearCLP(saldo)}
                        </td>

                        {/* Situación / Mora */}
                        <td>
                          <span className={`factura-badge ${estadoCobro}`}>
                            {estadoCobro}
                          </span>
                          {estaVencida && diasMora > 0 && (
                            <div style={{ fontSize: 10, color: 'var(--accent-red)', fontWeight: 700, marginTop: 4 }}>
                              🔴 {diasMora} días vencida
                            </div>
                          )}
                        </td>

                        {/* Acciones 1-Clic */}
                        <td>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                            {/* Botón Cobrar (Modal Plantilla formal + WhatsApp) */}
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setFacturaParaCobrar(factura)}
                              style={{
                                padding: '5px 8px',
                                fontSize: 11,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                              title="Generar carta formal y enviar WhatsApp de cobranza"
                            >
                              <IconoWhatsapp size={12} color="#25D366" />
                              <span>Cobrar</span>
                            </button>

                            {/* Botón Pagar (Liquidación 1-Clic) */}
                            {saldo > 0 ? (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setFacturaParaPagar(factura)}
                                style={{
                                  padding: '5px 8px',
                                  fontSize: 11,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  background: 'var(--accent-green)',
                                  borderColor: 'var(--accent-green)',
                                }}
                                title="Liquidar 100% o registrar abono express"
                              >
                                <IconoCheck size={12} />
                                <span>Pagar</span>
                              </button>
                            ) : null}

                            {/* Enlace al Proyecto si existe */}
                            {factura.proyecto && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => navegar(`/proyectos/${factura.proyecto!.id}`)}
                                style={{ padding: '5px 6px' }}
                                title="Ver ficha completa del proyecto"
                              >
                                <IconoDerecha size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Barra de Totales Agregados (Contrato 11.2) */}
          {facturas.data?.totales && (
            <div
              style={{
                marginTop: 16,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Mostrando <strong style={{ color: 'var(--text-primary)' }}>{facturas.data.datos.length}</strong> de{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{facturas.data.total}</strong> facturas
              </div>

              <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 6 }}>Facturado:</span>
                  <span className="td-mono" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {formatearCLP(facturas.data.totales.totalFacturado)}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 6 }}>Cobrado:</span>
                  <span className="td-mono" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent-green)' }}>
                    {formatearCLP(facturas.data.totales.totalCobrado)}
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 6 }}>Pendiente:</span>
                  <span className="td-mono" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent-yellow)' }}>
                    {formatearCLP(facturas.data.totales.saldoPorCobrar)}
                  </span>
                </div>

                {facturas.data.totales.saldoVencido > 0 && (
                  <div
                    style={{
                      background: 'var(--accent-red-soft)',
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <span style={{ fontSize: 11, color: 'var(--accent-red)', fontWeight: 600, marginRight: 6 }}>
                      Vencido:
                    </span>
                    <span className="td-mono" style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent-red)' }}>
                      {formatearCLP(facturas.data.totales.saldoVencido)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Acción 1-Clic: Pagar Factura */}
      {facturaParaPagar && (
        <ModalPagarFacturaRapido
          factura={facturaParaPagar}
          alCerrar={() => setFacturaParaPagar(null)}
          alPagarExitoso={() => {
            void facturas.refetch();
            void cartera.refetch();
          }}
        />
      )}

      {/* Modal Acción 1-Clic: Gestión de Cobranza (Plantilla Formal + WhatsApp) */}
      {facturaParaCobrar && (
        <ModalGestionCobro
          factura={facturaParaCobrar}
          alCerrar={() => setFacturaParaCobrar(null)}
          alAbrirPagar={() => {
            const f = facturaParaCobrar;
            setFacturaParaCobrar(null);
            setFacturaParaPagar(f);
          }}
        />
      )}
    </>
  );
}
